import type { FC } from "react";
import { useEffect, useState } from "react";

import { AddToDashboardModalV2 } from "pro-features/components/Modals/src/AddToDashboardModal-v2";
import { AddToDashboardButton } from "app/components/React/AddToDashboard/AddToDashboardButton";
import { allMetrics } from "app/components/widget/widget-config/metrics/metricsRegistry";
import type {
    IWidgetModel,
    IWidgetModelTypesFamily,
    IWidgetModelTypesType,
    IWidgetModelTypesWebSource,
} from "app/components/widget/widget-types/Widget";
import { WidgetModelAdapterService } from "app/components/widget/WidgetModelAdapterService";
import { i18nFilter } from "app/filters/i18nFilter";
import type { CampaignConfigForDashboard } from "app/pages/digital-marketing/keywords-campaigns/reports/keywords-report/keywords-table/KeywordsReportTypes";
import { allTrackers } from "app/services/track/track";

export interface IAddToDashboardProps {
    modelType?: string;
    webSource?: IWidgetModelTypesWebSource;
    type: IWidgetModelTypesType;
    metric: string;
    selectedSite?: boolean;
    filters?: any;
    width?: any;
    keys?: { id: string; image: string }[];
    family?: IWidgetModelTypesFamily;
    country?: string;
    customAsset?: string;
    overrideAddToDashboardParams?: Record<string, any>;
    industryQueryParams?: string;
    currentCampaignConfiguration?: CampaignConfigForDashboard;
    buttonProps?: {
        isDisabled?: boolean;
        tooltipContent?: string | null;
    };
    duration?: string;
}

const getCustomModel = async ({
    modelType,
    webSource,
    type,
    metric,
    filters,
    selectedSite,
    customAsset,
    currentCampaignConfiguration,
    overrideAddToDashboardParams,
    family,
    keys,
    width,
    duration,
    country,
}: Omit<IAddToDashboardProps, "buttonProps">): Promise<IWidgetModel> => {
    let model;
    if (modelType === "fromKeyword") {
        model = await WidgetModelAdapterService.getInstance().fromKeyword(
            metric,
            type,
            webSource,
            filters,
            keys,
            family,
            customAsset,
        );
    } else if (modelType === "fromWebsite") {
        model = WidgetModelAdapterService.getInstance().fromWebsite(
            metric,
            type,
            webSource,
            selectedSite,
            filters,
            duration,
        );
    } else if (modelType === "fromMobile") {
        model = WidgetModelAdapterService.getInstance().fromMobile(metric, type, webSource, width, filters);
        //if modelType==undefined should pass keys prop as => [key]
    } else if (modelType === "fromSegment") {
        model = WidgetModelAdapterService.getInstance().fromSegment(metric, type, webSource, filters, selectedSite);
    } else if (modelType === "fromRankTracker") {
        model = WidgetModelAdapterService.getInstance().fromRankTracker(
            metric,
            type,
            keys,
            width,
            webSource,
            currentCampaignConfiguration,
            country,
            duration,
            filters,
        );
    } else if (modelType === "fromAdIntelligence") {
        model = WidgetModelAdapterService.getInstance().fromAdIntelligence(
            metric,
            type,
            keys,
            webSource,
            country,
            duration,
            customAsset,
            filters,
        );
    } else {
        model = WidgetModelAdapterService.getInstance().noModelType(
            metric,
            type,
            webSource,
            filters,
            keys,
            family,
            customAsset,
        );
    }

    return {
        ...model,
        ...overrideAddToDashboardParams,
    } as IWidgetModel;
};

export const useGetCustomModel = (props: IAddToDashboardProps): IWidgetModel | null => {
    const [customModel, setCustomModel] = useState<IWidgetModel | null>(null);

    const propsString = JSON.stringify(props);

    useEffect(() => {
        const fetchCustomModel = async () => {
            const model = await getCustomModel(props);
            setCustomModel(model);
        };
        void fetchCustomModel();
    }, [propsString]);

    return customModel;
};

export const AddToDashboard: FC<IAddToDashboardProps> = ({ buttonProps, ...props }) => {
    const [isOpen, setIsOpen] = useState(false);
    const customModel = useGetCustomModel(props);

    function handleClose() {
        setIsOpen(false);
    }

    function handleOpenDashboard() {
        setIsOpen(true);
        if (customModel?.metric) {
            const metricConfig = allMetrics[customModel.metric];
            allTrackers.trackEvent(
                "Pop up",
                "open",
                `Add to my Dashboard/${i18nFilter()((metricConfig?.properties?.title as string) || "")}`,
            );
        }
    }

    return (
        <>
            <AddToDashboardButton
                onClick={handleOpenDashboard}
                {...buttonProps}
            />
            {isOpen ? (
                <AddToDashboardModalV2
                    customModel={customModel}
                    onConfirm={handleClose}
                    onCancel={handleClose}
                />
            ) : null}
        </>
    );
};
